import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Button,
  Card,
  Empty,
  Select,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';

import {
  attachAssessmentScenario,
  fetchAssessmentResultDetail,
  fetchAssessmentResults,
  fetchAssessmentScenarios,
  fetchAssessmentState,
  recomputeAssessment,
  selectors,
  AssessmentResultRow,
  ADMIN,
  FINISHED,
} from 'state';
import { formatters } from 'services';
import {
  AnswerKeyRating,
  ConvictionCoverageNote,
  LoadingScreen,
  ParticipationNote,
  VerdictBanner,
  gateShortLabel,
  readAnswerKeyGate,
} from 'components';
import type { TableColumnsType as ColumnsType } from 'antd';

import Explainer, { summaryExplainer } from './explainers';
import PillarTab from './PillarTab';
import DebugTab from './DebugTab';
import './assessment_report.css';

// A NULL IS NOT A ZERO. The report lists everybody who was in the room,
// including candidates the evidence gate refused to mark, so a score column
// with no score reaches this table. The number formatter underneath turns an
// absent value into 0, which would print "0.00" beside "Not assessed" on the
// one page a trainer shows a hiring customer. A dash is the only honest thing
// to print, and it is what the administrator's grid already prints.
const score = (value: number | null | undefined) =>
  value == null ? '—' : formatters.commas2Digits(value);
// Sorting: an absent score sorts below every real one, in both directions.
const num = (value: number | null | undefined) =>
  value == null ? Number.NEGATIVE_INFINITY : value;

const ratingColor = (rating: string) => {
  if (!rating) return 'default';
  if (rating.startsWith('A')) return 'green';
  if (rating.startsWith('B')) return 'blue';
  if (rating.startsWith('C')) return 'orange';
  return 'red';
};

// Human pillar names; the trailing * keeps the provisional convention (see the
// table footer note). Tooltips carry the full pillar names.
const PillarTitle: React.FC<{
  label: string;
  full: string;
  provisional?: boolean;
}> = ({ label, full, provisional }) => (
  <Tooltip title={full}>
    <span>{provisional ? `${label} *` : label}</span>
  </Tooltip>
);

/**
 * A row in the attach picker, as this screen reads it.
 *
 * The extra fields are served alongside the ones the redux type declares and
 * are read defensively: an endpoint that has not started sending them yet
 * falls back to the answer key's own name and code rather than printing a gap.
 */
type PickerScenario = {
  code: string;
  name: string;
  model_version: string;
  periods: number;
  /** The blueprint family this key was derived from. */
  blueprint_name?: string | null;
  /** The identity hash (D5). Shown eight characters wide, as everywhere else. */
  identity_hash?: string | null;
  /** When the key was derived. */
  built_at?: string | null;
};

/**
 * THE ANSWER KEY, NAMED THE WAY A TRAINER CAN CHECK IT (D5).
 *
 * There are no version numbers any more: a key IS its inputs, and the hash
 * over them is its identity. `v conviction-v0` was on every row in the fleet
 * and told a trainer nothing; the blueprint they recognise, the eight
 * characters that name this key on the blueprint page, and the day it was
 * built let them go and look.
 */
export const pickerLabel = (scenario: PickerScenario): string => {
  const hash8 = (scenario.identity_hash || '').slice(0, 8);
  const built = scenario.built_at ? new Date(scenario.built_at) : null;
  const builtOn =
    built && !Number.isNaN(built.getTime())
      ? `built ${built.toLocaleDateString()}`
      : '';
  return [
    scenario.blueprint_name || scenario.name,
    hash8 || scenario.code,
    builtOn,
    `${scenario.periods} periods`,
  ]
    .filter(Boolean)
    .join(' · ');
};

/** Trainer/admin assessment surface: attach a scenario pre-session; once the
 *  session is finished, a tabbed report — ranked summary plus a per-pillar,
 *  per-player breakdown with plain-language explainers. */
const Assessment: React.FC = () => {
  const dispatch = useDispatch();
  const userType = useSelector(selectors.userType);
  const session = useSelector(selectors.activeSession);
  const assessment = useSelector(selectors.assessment);
  const results = useSelector(selectors.assessmentResults);
  const resultDetails = useSelector(selectors.assessmentResultDetails);
  const scenarios = useSelector(selectors.assessmentScenarios);
  const attaching = useSelector(selectors.assessmentAttaching);

  // The validity verdict of the answer key these scores were graded against.
  // `null` when the endpoint did not serve it, and the banner then says
  // nothing at all -- see components/VerdictBanner/gate.ts.
  const answerKeyGate = useMemo(
    () => readAnswerKeyGate({ answer_key_gate: results.answerKeyGate }),
    [results.answerKeyGate],
  );

  const [selectedScenario, setSelectedScenario] = useState<string>();
  // Shared across the pillar tabs so switching tabs keeps the player.
  const [selectedPlayer, setSelectedPlayer] = useState<number>();

  const isFinished = session.status === FINISHED;
  const deferred =
    assessment.capture_only === true ||
    results.capture_only === true ||
    assessment.status === 'deferred' ||
    results.status === 'deferred';

  useEffect(() => {
    if (!session?.id) return;
    dispatch(fetchAssessmentState({}));
  }, [dispatch, session?.id]);

  useEffect(() => {
    if (!session?.id) return;
    if (scenarios.fetched || scenarios.fetching) return;
    dispatch(fetchAssessmentScenarios({}));
  }, [dispatch, session?.id, scenarios.fetched, scenarios.fetching]);

  // Refetch the snapshot after an attach round-trip completes (true -> false).
  const wasAttaching = useRef(false);
  useEffect(() => {
    if (wasAttaching.current && !attaching) {
      dispatch(fetchAssessmentState({}));
    }
    wasAttaching.current = attaching;
  }, [dispatch, attaching]);

  useEffect(() => {
    if (!session?.id || !assessment.attached) return;
    dispatch(fetchAssessmentResults({}));
  }, [dispatch, session?.id, assessment.attached]);

  // Default the pillar-tab player to the top-ranked result.
  useEffect(() => {
    if (selectedPlayer !== undefined || !results.items.length) return;
    setSelectedPlayer(results.items[0].player);
  }, [results.items, selectedPlayer]);

  // Per-player detail (one request per player, cached by id) feeds the PnL /
  // NAV columns below and pre-warms the pillar tabs. `fetching[player]`
  // present means requested (true) or failed (false) — never re-dispatch
  // here; refreshing results clears failed markers for a retry.
  useEffect(() => {
    if (!assessment.attached || deferred) return;
    results.items.forEach(row => {
      if (resultDetails.items[row.player]) return;
      if (resultDetails.fetching[row.player] !== undefined) return;
      dispatch(fetchAssessmentResultDetail({ player: row.player }));
    });
  }, [dispatch, assessment.attached, deferred, results.items, resultDetails]);

  const columns: ColumnsType<AssessmentResultRow> = useMemo(
    () => [
      {
        title: 'Rank',
        dataIndex: 'rank',
        key: 'rank',
        width: 70,
        sorter: (a, b) => num(a.rank) - num(b.rank),
        defaultSortOrder: 'ascend',
      },
      {
        title: 'Player',
        dataIndex: 'player_name',
        key: 'player_name',
      },
      {
        title: (
          <PillarTitle
            label="Signal"
            full="Pillar 1 — Signal Extraction & Causal Reasoning"
          />
        ),
        dataIndex: 'pillar_1',
        key: 'pillar_1',
        render: score,
        sorter: (a, b) => num(a.pillar_1) - num(b.pillar_1),
      },
      {
        title: (
          <PillarTitle
            label="Expression"
            full="Pillar 2 — Expression Quality"
          />
        ),
        dataIndex: 'pillar_2',
        key: 'pillar_2',
        render: score,
        sorter: (a, b) => num(a.pillar_2) - num(b.pillar_2),
      },
      {
        title: (
          <PillarTitle
            label="Risk Construction"
            full="Pillar 3 — Portfolio Risk Construction"
          />
        ),
        dataIndex: 'pillar_3',
        key: 'pillar_3',
        render: score,
        sorter: (a, b) => num(a.pillar_3) - num(b.pillar_3),
      },
      {
        title: (
          <PillarTitle
            label="Updating"
            full="Pillar 4 — Updating & Selectivity (provisional)"
            provisional
          />
        ),
        dataIndex: 'pillar_4_provisional',
        key: 'pillar_4_provisional',
        render: score,
        sorter: (a, b) =>
          num(a.pillar_4_provisional) - num(b.pillar_4_provisional),
      },
      {
        title: (
          <PillarTitle
            label="Tail & Liquidity"
            full="Pillar 5 — Liquidity & Tail Resilience (provisional)"
            provisional
          />
        ),
        dataIndex: 'pillar_5_provisional',
        key: 'pillar_5_provisional',
        render: score,
        sorter: (a, b) =>
          num(a.pillar_5_provisional) - num(b.pillar_5_provisional),
      },
      {
        title: 'Official composite',
        dataIndex: 'official_composite',
        key: 'official_composite',
        render: score,
        sorter: (a, b) => num(a.official_composite) - num(b.official_composite),
      },
      {
        title: (
          <Tooltip title="Composite including the provisional pillars — illustrative only.">
            <span>Illustrative *</span>
          </Tooltip>
        ),
        dataIndex: 'illustrative_composite',
        key: 'illustrative_composite',
        render: score,
        sorter: (a, b) =>
          num(a.illustrative_composite) - num(b.illustrative_composite),
      },
      {
        title: 'Rating',
        dataIndex: 'rating',
        key: 'rating',
        // Suppressed while the answer key behind the score failed its own
        // quality audit and nobody signed for it: the failing check is
        // precisely the finding that a candidate expressing no view can pass,
        // so a hiring band printed here would be a claim the evidence refuses.
        render: (rating: string) => (
          <AnswerKeyRating
            asTag
            rating={rating}
            gate={answerKeyGate}
            showDiagnostics={userType === ADMIN}
            color={ratingColor(rating)}
          />
        ),
      },
      {
        // WHAT THE MARK WAS EARNED ON, beside the mark. The report now lists
        // everybody who was in the room, and pillar 1 -- a third of the
        // official composite -- is earned entirely on the conviction sliders
        // and the news-assignment panel, so a row here no longer implies a
        // trade. Never a warning colour: a candidate who voted and did not
        // trade has done nothing wrong, and neither has the score.
        title: (
          <Tooltip title="What the candidate did in the room. Pillar 1 — news mapping and conviction — needs no trade, so a score can be earned without one.">
            <span>Participation</span>
          </Tooltip>
        ),
        dataIndex: 'participation',
        key: 'participation',
        render: (participation: string, row: AssessmentResultRow) => (
          <>
            <ParticipationNote asTag participation={participation} />
            <ConvictionCoverageNote asTag coverage={row.conviction_coverage} />
          </>
        ),
      },
      // Context-only columns from the per-player detail endpoint; dash until
      // that player's detail has loaded (or while the endpoint isn't live).
      {
        title: (
          <Tooltip title="Realised + unrealised PnL over the session — context only, not scored.">
            <span>Total PnL ($m)</span>
          </Tooltip>
        ),
        key: 'total_pnl_m',
        className: 'assessment-report-num',
        render: (_: unknown, row: AssessmentResultRow) => {
          const detail = resultDetails.items[row.player];
          return detail
            ? formatters.commasExact1Digit(detail.summary?.total_pnl_m)
            : '—';
        },
      },
      {
        title: (
          <Tooltip title="Net asset value at the end of the session — context only, not scored.">
            <span>Ending NAV ($m)</span>
          </Tooltip>
        ),
        key: 'ending_nav_m',
        className: 'assessment-report-num',
        render: (_: unknown, row: AssessmentResultRow) => {
          const detail = resultDetails.items[row.player];
          return detail
            ? formatters.commasExact1Digit(detail.summary?.ending_nav_m)
            : '—';
        },
      },
    ],
    [resultDetails.items, answerKeyGate, userType],
  );

  if (!assessment.fetched && assessment.fetching) return <LoadingScreen />;

  if (!assessment.attached) {
    // Only ACTIVE definitions are offered. A draft has not been through the
    // validity gate and is still being edited, so attaching one would grade a
    // class against something that can change underneath them.
    const attachable = scenarios.items.filter(item => item.status === 'active');
    return (
      <Card title="Assessment" style={{ maxWidth: 640 }}>
        <Typography.Paragraph type="secondary">
          No assessment is attached to this session. Attach a scenario to enable
          the player assessment panel (news mapping, conviction sliders and the
          live risk panel). Each scenario brings its own answer key — the
          reviewed record the class will be scored against.
        </Typography.Paragraph>
        <Select
          style={{ width: 360, marginRight: 10 }}
          placeholder="Select a scenario"
          loading={scenarios.fetching}
          value={selectedScenario}
          onChange={setSelectedScenario}
          notFoundContent={
            scenarios.fetching
              ? undefined
              : 'No active scenario is available to attach.'
          }
          options={attachable.map(s => {
            // A scenario row that carries its answer key's quality-audit
            // verdict says so in the label, so a trainer is warned at the
            // moment of choosing rather than after the class has played it.
            // Silent when the endpoint does not serve the verdict.
            const gate = readAnswerKeyGate(s);
            const warning =
              userType === ADMIN && gate && gate.state !== 'clear'
                ? ` · ${gateShortLabel(gate)}`
                : '';
            return { value: s.code, label: `${pickerLabel(s)}${warning}` };
          })}
        />
        <Button
          type="primary"
          disabled={!selectedScenario}
          loading={attaching}
          onClick={() =>
            selectedScenario &&
            dispatch(attachAssessmentScenario({ scenario: selectedScenario }))
          }
        >
          Attach
        </Button>
      </Card>
    );
  }

  const noData = (
    <Empty
      description={
        <Typography.Text type="secondary">
          {isFinished
            ? 'No results yet — run a recompute below.'
            : 'Results are computed when the session finishes.'}
        </Typography.Text>
      }
    />
  );

  const summaryTab = deferred ? (
    <Card>
      <Typography.Paragraph>
        Your responses are saved. Results pending review.
      </Typography.Paragraph>
      <Button
        onClick={() => {
          dispatch(fetchAssessmentState({}));
          dispatch(fetchAssessmentResults({}));
        }}
      >
        Refresh
      </Button>
    </Card>
  ) : (
    <div className="assessment-report-tab">
      {userType === ADMIN && <VerdictBanner gate={answerKeyGate} />}
      {(userType === ADMIN || !answerKeyGate?.suppressRating) && (
        <Explainer
          copy={summaryExplainer(userType === ADMIN ? answerKeyGate : null)}
        />
      )}
      <Table
        dataSource={results.items}
        columns={columns}
        rowKey="id"
        bordered
        loading={results.fetching}
        locale={{ emptyText: noData }}
        pagination={false}
        footer={() => (
          <div>
            <Button onClick={() => dispatch(fetchAssessmentResults({}))}>
              Refresh
            </Button>
            <Button
              style={{ marginLeft: 10 }}
              loading={results.recomputing}
              onClick={() => dispatch(recomputeAssessment({}))}
            >
              Recompute
            </Button>
            <Typography.Text type="secondary" style={{ marginLeft: 12 }}>
              * provisional — shown for context, excluded from the official
              composite.
            </Typography.Text>
          </div>
        )}
      />
    </div>
  );

  const pillarTab = (pillar: '1' | '2' | '3' | '4' | '5') => (
    <PillarTab
      pillar={pillar}
      players={results.items}
      player={selectedPlayer}
      onPlayerChange={setSelectedPlayer}
    />
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
        <Typography.Title level={4}>Assessment Results</Typography.Title>
        {(!deferred || userType === ADMIN) && <Tag>{assessment.scenario}</Tag>}
        {(deferred || results.status) && (
          <Tag color="blue">
            {deferred && userType !== ADMIN
              ? 'Results pending review'
              : results.status || 'deferred'}
          </Tag>
        )}
        {!deferred && results.computed_at && (
          <Typography.Text type="secondary">
            computed {formatters.dateWithTime(results.computed_at)}
          </Typography.Text>
        )}
      </div>
      {!deferred &&
        userType === ADMIN &&
        results.status === 'failed' &&
        results.error && (
          <Alert
            type="error"
            showIcon
            message="Last computation failed"
            description={results.error}
            style={{ marginBottom: 12 }}
          />
        )}
      <Tabs
        defaultActiveKey="summary"
        items={[
          { key: 'summary', label: 'Summary', children: summaryTab },
          ...(!deferred
            ? [
                { key: '1', label: 'Signal', children: pillarTab('1') },
                { key: '2', label: 'Expression', children: pillarTab('2') },
                {
                  key: '3',
                  label: 'Risk Construction',
                  children: pillarTab('3'),
                },
                { key: '4', label: 'Updating *', children: pillarTab('4') },
                {
                  key: '5',
                  label: 'Tail & Liquidity *',
                  children: pillarTab('5'),
                },
              ]
            : []),
          // Raw engine/resolver inspection surface — admins only (the auth
          // userType gate, same signal Routes.tsx uses).
          ...(userType === ADMIN
            ? [{ key: 'debug', label: 'Debug *', children: <DebugTab /> }]
            : []),
        ]}
      />
    </div>
  );
};

export default Assessment;
